import { useState } from 'react';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import { Box, IconButton, keyframes, Theme } from '@mui/material';
import { getStateLayerColor, StateLayer } from '../../theme/color.utils';

export type FavoriteButtonVariant = 'small' | 'medium';

export interface FavoriteButtonProps {
  isActive: boolean;
  disabled?: boolean;
  onClick: () => void;
  variant?: FavoriteButtonVariant;
}

// Animation: the heart pops with a bounce
const pop = keyframes`
  0%   { transform: scale(1); }
  15%  { transform: scale(0); }
  45%  { transform: scale(1.35); }
  70%  { transform: scale(0.9); }
  85%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

// Animation: the heart fills with color from bottom to top
const fillRise = keyframes`
  0%   { clip-path: inset(100% 0 0 0); }
  100% { clip-path: inset(0 0 0 0); }
`;

// Animation: small hearts fly out of the button and slowly fade away
const flyOut = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  100% {
    transform:
      translate(calc(-50% + var(--tx)), calc(-50% + var(--ty)))
      scale(var(--scale))
      rotate(var(--rot));
    opacity: 0;
  }
`;

const PARTICLE_COUNT = 8;

interface Particle {
  id: number;
  tx: string;
  ty: string;
  scale: number;
  rot: string;
  duration: string;
  delay: string;
}

function createParticles(seed: number): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    // Animation: spread the small hearts in an upward fan with slight randomness
    const baseAngle = (-180 + (i / (PARTICLE_COUNT - 1)) * 180) * (Math.PI / 180);
    const jitter = (Math.random() - 0.5) * 0.5;
    const angle = baseAngle + jitter;
    const distance = 34 + Math.random() * 26;
    return {
      id: seed * 100 + i,
      tx: `${Math.cos(angle) * distance}px`,
      ty: `${Math.sin(angle) * distance}px`,
      scale: 0.5 + Math.random() * 0.7,
      rot: `${(Math.random() - 0.5) * 120}deg`,
      duration: `${650 + Math.random() * 350}ms`,
      delay: `${Math.random() * 120}ms`,
    };
  });
}

export default function FavoriteButton({
  isActive,
  disabled = false,
  onClick,
  variant = 'medium',
}: FavoriteButtonProps): React.JSX.Element {
  const buttonSize = variant === 'small' ? 32 : 40;
  const iconSize = variant === 'small' ? 20 : 24;

  const [particles, setParticles] = useState<Particle[]>([]);
  const [pulse, setPulse] = useState(0);

  const handleClick = (): void => {
    if (!isActive) {
      setPulse((p) => p + 1);
      setParticles(createParticles(pulse + 1));
    }
    onClick();
  };

  const showFill = disabled ? !isActive : isActive;

  return (
    <IconButton
      size={variant}
      disabled={disabled}
      onClick={handleClick}
      sx={(theme: Theme) => {
        const containerColor = theme.palette.surfaceContainerLowest.main;
        const iconColor = showFill
          ? theme.palette.favorite.main
          : theme.palette.onSurfaceVariant.main;

        return {
          position: 'relative',
          overflow: 'visible',
          width: buttonSize,
          height: buttonSize,
          borderRadius: '100px',
          color: iconColor,
          backgroundColor: containerColor,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            color: iconColor,
            backgroundColor: getStateLayerColor(StateLayer.Layer8, containerColor, iconColor),
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&:disabled': {
            color: iconColor,
            backgroundColor: containerColor,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
          '&:focus': {
            color: iconColor,
            backgroundColor: containerColor,
          },
          '&:focus:not(:focus-visible)': {
            backgroundColor: containerColor,
          },
          '&:active': {
            color: iconColor,
            backgroundColor: getStateLayerColor(StateLayer.Layer12, containerColor, iconColor),
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          },
        };
      }}
    >
      
      <Box        
        key={pulse}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          width: iconSize,
          height: iconSize,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          animation: pulse > 0 ? `${pop} 600ms ease-out` : 'none',
        }}
      >

        <FavoriteBorderOutlinedIcon
          sx={{
            position: 'absolute',
            inset: 0,
            fontSize: iconSize,
          }}
        />

        <FavoriteOutlinedIcon
          sx={(theme: Theme) => ({
            position: 'absolute',
            inset: 0,
            fontSize: iconSize,
            color: theme.palette.favorite.main,
            opacity: showFill ? 1 : 0,
            transition: 'opacity 200ms ease-out',
            willChange: 'clip-path, opacity',
            animation: pulse > 0 ? `${fillRise} 450ms ease-out` : 'none',
          })}
        />
      </Box>

      
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 0,
          height: 0,
          pointerEvents: 'none',
          transform: 'translateZ(0)',
          zIndex: 1,
        }}
      >
        {particles.map((p) => (
          <FavoriteOutlinedIcon
            key={p.id}
            sx={(theme: Theme) => ({
              position: 'absolute',
              top: 0,
              left: 0,
              fontSize: 14,
              color: theme.palette.favorite.main,
              opacity: 0,
              willChange: 'transform, opacity',
              '--tx': p.tx,
              '--ty': p.ty,
              '--scale': p.scale,
              '--rot': p.rot,
              animation: `${flyOut} ${p.duration} ease-out ${p.delay} forwards`,
            })}
          />
        ))}
      </Box>
    </IconButton>
  );
}
